import type {
  Invite,
  JoinRequest,
  Member,
  PaidSpaceSlot,
  Profile,
  Report,
  Room,
  Space,
  SpaceRole
} from "../../shared/api/types";

export interface AdminMetric {
  readonly title: string;
  readonly value: string;
  readonly note: string;
}

export interface AdminTableRow {
  readonly label: string;
  readonly detail: string;
  readonly status: string;
}

export type MockPlanLabel = "Free Space" | "Plus Space" | "Paid Space";
export type MockRelayStatus = "healthy" | "degraded" | "standard" | "priority";

export interface MockSpace extends Space {
  readonly plan_label: MockPlanLabel;
  readonly retention_options?: readonly [7, 14, 30];
  readonly relay_status: MockRelayStatus;
}

export interface MockReport extends Report {
  readonly submitted_plaintext: string;
  readonly ai_moderation_result: string;
  readonly review_note: string;
}

export interface MockJoinRequest extends JoinRequest {
  readonly join_method: "code" | "link" | "qr";
  readonly invite_source: string;
  readonly application_answer: string;
  readonly verification_state: "not_checked" | "device_seen" | "verified_by_admin";
  readonly device_fingerprint: string;
  readonly profile_age_label: string;
  readonly prior_ban_in_space: boolean;
}

export interface MockMember extends Member {
  readonly status: "active" | "removed" | "banned";
  readonly guest_expires_at: string | null;
  readonly device_fingerprint: string;
}

export interface MockRoom extends Room {
  readonly room_type: "standard" | "role_based";
  readonly frozen: boolean;
  readonly notice: string | null;
}

export interface MockInvite extends Invite {
  readonly label: string;
  readonly raw_token_once: string | null;
  readonly guest_pass_expires_at?: string | null;
}

export interface FreeLimitModeExample {
  readonly space_id: string;
  readonly space_name: string;
  readonly started_at: string;
  readonly grace_days_total: number;
  readonly grace_days_remaining: number;
  readonly reason: string;
}

export interface MockAdminData {
  readonly profile: Profile;
  readonly spaces: readonly MockSpace[];
  readonly roomsBySpaceId: Readonly<Record<string, readonly MockRoom[]>>;
  readonly membersBySpaceId: Readonly<Record<string, readonly MockMember[]>>;
  readonly invitesBySpaceId: Readonly<Record<string, readonly MockInvite[]>>;
  readonly joinRequestsBySpaceId: Readonly<Record<string, readonly MockJoinRequest[]>>;
  readonly reports: readonly MockReport[];
  readonly paidSpaceSlots: readonly PaidSpaceSlot[];
  readonly freeLimitModeExample: FreeLimitModeExample;
}

export interface SpaceDashboardRow {
  readonly spaceId: string;
  readonly name: string;
  readonly planState: string;
  readonly memberCount: number;
  readonly memberCap: number;
  readonly roomCount: number;
  readonly roomCap: number;
  readonly retentionLabel: string;
  readonly role: string;
  readonly relayStatus: string;
  readonly freeLimitMode: boolean;
}

export interface SpaceAdminDetail {
  readonly space: MockSpace;
  readonly rooms: readonly MockRoom[];
  readonly members: readonly MockMember[];
  readonly invites: readonly MockInvite[];
  readonly joinRequests: readonly MockJoinRequest[];
  readonly reports: readonly MockReport[];
  readonly paidSlot: PaidSpaceSlot | null;
  readonly currentUserRole: string;
  readonly memberCount: number;
  readonly roomCount: number;
  readonly pendingJoinRequestCount: number;
  readonly openReportCount: number;
  readonly retentionLabel: string;
  readonly relayStatusLabel: string;
}

export const MOCK_PLAN_LIMITS = {
  free: {
    active_space_limit: 3,
    member_cap: 10,
    room_cap: 2,
    retention_days: 7
  },
  plus: {
    active_space_limit: 10,
    member_cap: 15,
    room_cap: 3,
    retention_days: 7
  },
  paid: {
    member_cap: 25,
    room_cap: 8,
    retention_options: [7, 14, 30] as const
  }
} as const;

const NOW = "2026-06-16T09:00:00Z";

const profile: Profile = {
  profile_id: "pro_mock_owner",
  display_name: "Maya Chen",
  handle: "maya",
  created_at: "2026-04-01T08:00:00Z",
  updated_at: NOW
};

export const MOCK_SPACES: readonly MockSpace[] = [
  {
    space_id: "spc_free_field_ops",
    name: "Field Operations Team",
    description: "Daily check-ins for a small volunteer group.",
    template_type: "community",
    visibility: "private",
    join_approval_required: true,
    local_discovery_enabled: false,
    plan_state: "free",
    plan_label: "Free Space",
    relay_status: "standard",
    paid_slot_id: null,
    member_cap: MOCK_PLAN_LIMITS.free.member_cap,
    room_cap: MOCK_PLAN_LIMITS.free.room_cap,
    retention_days: MOCK_PLAN_LIMITS.free.retention_days,
    free_limit_mode: true,
    free_limit_mode_started_at: "2026-06-12T09:00:00Z",
    created_at: "2026-04-02T08:00:00Z",
    updated_at: NOW
  },
  {
    space_id: "spc_plus_training",
    name: "Training Workshop",
    description: "A Plus Space for workshop updates and follow-up.",
    template_type: "education",
    visibility: "private",
    join_approval_required: true,
    local_discovery_enabled: false,
    plan_state: "plus",
    plan_label: "Plus Space",
    relay_status: "healthy",
    paid_slot_id: null,
    member_cap: MOCK_PLAN_LIMITS.plus.member_cap,
    room_cap: MOCK_PLAN_LIMITS.plus.room_cap,
    retention_days: MOCK_PLAN_LIMITS.plus.retention_days,
    free_limit_mode: false,
    free_limit_mode_started_at: null,
    created_at: "2026-04-10T08:00:00Z",
    updated_at: NOW
  },
  {
    space_id: "spc_paid_market_ops",
    name: "Market Operations Crew",
    description: "A paid Space for operators, stewards, and short-term guests.",
    template_type: "event",
    visibility: "private",
    join_approval_required: true,
    local_discovery_enabled: false,
    plan_state: "spaces_paid",
    plan_label: "Paid Space",
    relay_status: "priority",
    paid_slot_id: "slot_mock_1",
    member_cap: MOCK_PLAN_LIMITS.paid.member_cap,
    room_cap: MOCK_PLAN_LIMITS.paid.room_cap,
    retention_days: 14,
    retention_options: MOCK_PLAN_LIMITS.paid.retention_options,
    free_limit_mode: false,
    free_limit_mode_started_at: null,
    created_at: "2026-05-01T08:00:00Z",
    updated_at: NOW
  }
];

export const MOCK_ROOMS_BY_SPACE_ID: MockAdminData["roomsBySpaceId"] = {
  spc_free_field_ops: [
    room("room_free_general", "spc_free_field_ops", "General", "Main updates", 4),
    room("room_free_schedule", "spc_free_field_ops", "Schedule", "Upcoming shifts", 4)
  ],
  spc_plus_training: [
    room("room_plus_general", "spc_plus_training", "General", "Workshop updates", 6),
    room("room_plus_questions", "spc_plus_training", "Questions", "Participant questions", 6),
    room("room_plus_resources", "spc_plus_training", "Resources", "Short retained notes", 6)
  ],
  spc_paid_market_ops: [
    room("room_paid_general", "spc_paid_market_ops", "General", "Crew-wide updates", 12),
    room("room_paid_stewards", "spc_paid_market_ops", "Stewards", "Steward coordination", 12, true, ["owner", "admin", "moderator"]),
    room("room_paid_guest_info", "spc_paid_market_ops", "Guest Info", "Temporary guest updates", 12, true, ["owner", "admin", "moderator", "guest"]),
    room("room_paid_reports", "spc_paid_market_ops", "Reports", "Admin review queue notes", 12, true, ["owner", "admin", "moderator"])
  ]
};

export const MOCK_MEMBERS_BY_SPACE_ID: MockAdminData["membersBySpaceId"] = {
  spc_free_field_ops: [
    member("mem_free_owner", "spc_free_field_ops", "pro_mock_owner", "owner", "Maya Chen", "maya", "A1BC-23DE"),
    member("mem_free_admin", "spc_free_field_ops", "pro_ari", "admin", "Ari Khan", "ari", "B7RM-41QA"),
    member("mem_free_mod", "spc_free_field_ops", "pro_tess", "moderator", "Tess Morgan", "tess", "K9TT-80PL"),
    member("mem_free_1", "spc_free_field_ops", "pro_lee", "member", "Lee Park", "lee", "N2QP-72LA"),
    member("mem_free_2", "spc_free_field_ops", "pro_jules", "member", "Jules Reid", "jules", "R4JX-11ZA"),
    member("mem_free_3", "spc_free_field_ops", "pro_sam", "member", "Sam Patel", "sam", "S3MP-09AA"),
    member("mem_free_4", "spc_free_field_ops", "pro_nina", "member", "Nina Wright", "nina", "N1NA-55WR"),
    member("mem_free_5", "spc_free_field_ops", "pro_ravi", "member", "Ravi Singh", "ravi", "R8VI-64SG"),
    member("mem_free_6", "spc_free_field_ops", "pro_lena", "member", "Lena Stone", "lena", "L3NA-29ST"),
    member("mem_free_7", "spc_free_field_ops", "pro_omar", "member", "Omar Vale", "omar", "O7MR-13VL"),
    member("mem_free_8", "spc_free_field_ops", "pro_ella", "member", "Ella Quinn", "ella", "E2LA-88QN")
  ],
  spc_plus_training: [
    member("mem_plus_owner", "spc_plus_training", "pro_mock_owner", "owner", "Maya Chen", "maya", "A1BC-23DE"),
    member("mem_plus_admin", "spc_plus_training", "pro_noah", "admin", "Noah Blake", "noah", "N0AH-61BK"),
    member("mem_plus_mod", "spc_plus_training", "pro_ivy", "moderator", "Ivy Cole", "ivy", "I4VY-73CL"),
    member("mem_plus_1", "spc_plus_training", "pro_mina", "member", "Mina Hall", "mina", "M1NA-44HL"),
    member("mem_plus_2", "spc_plus_training", "pro_eli", "member", "Eli Fox", "eli", "E1FX-02OX")
  ],
  spc_paid_market_ops: [
    member("mem_paid_owner", "spc_paid_market_ops", "pro_mock_owner", "owner", "Maya Chen", "maya", "A1BC-23DE"),
    member("mem_paid_admin", "spc_paid_market_ops", "pro_rio", "admin", "Rio Lane", "rio", "R1O0-23LN"),
    member("mem_paid_mod", "spc_paid_market_ops", "pro_bea", "moderator", "Bea Grant", "bea", "B3A0-74GT"),
    member("mem_paid_1", "spc_paid_market_ops", "pro_cleo", "member", "Cleo Moss", "cleo", "C1EO-34MS"),
    member("mem_paid_2", "spc_paid_market_ops", "pro_ash", "member", "Ash Dean", "ash", "A5SH-81DN"),
    member("mem_paid_guest", "spc_paid_market_ops", "pro_kit", "guest", "Kit Bell", "kit", "K1TB-55BL", "2026-06-30T23:59:00Z")
  ]
};

export const MOCK_INVITES_BY_SPACE_ID: MockAdminData["invitesBySpaceId"] = {
  spc_free_field_ops: [
    invite("inv_free_code", "spc_free_field_ops", "code", "ABCD-****", 4, "Main join code", "ABCD-1234"),
    invite("inv_free_link", "spc_free_field_ops", "link", "link-****-ops", 9, "Volunteer invite link", "https://booplyim.com/j/free-ops"),
    invite("inv_free_qr", "spc_free_field_ops", "qr", "qr-****-ops", 12, "Field QR invite", "qr-field-ops")
  ],
  spc_plus_training: [
    invite("inv_plus_code", "spc_plus_training", "code", "WKS9-****", 2, "Workshop code", "WKS9-1133"),
    invite("inv_plus_link", "spc_plus_training", "link", "link-****-training", 5, "Workshop link", "https://booplyim.com/j/training")
  ],
  spc_paid_market_ops: [
    invite("inv_paid_qr", "spc_paid_market_ops", "qr", "qr-****-market", 18, "Market QR invite", "qr-market-poster"),
    invite("inv_paid_link", "spc_paid_market_ops", "link", "link-****-market", 6, "Vendor invite link", "https://booplyim.com/j/market"),
    invite("inv_paid_code", "spc_paid_market_ops", "code", "MKT7-****", 3, "Steward join code", "MKT7-2244"),
    invite("inv_paid_guest", "spc_paid_market_ops", "qr", "guest-****-market", 1, "Guest Pass example", "guest-pass-market", "2026-06-30T23:59:00Z")
  ]
};

export const MOCK_JOIN_REQUESTS_BY_SPACE_ID: MockAdminData["joinRequestsBySpaceId"] = {
  spc_free_field_ops: [
    joinRequest({
      application_answer: "I can help with Saturday setup and need the schedule room.",
      device_fingerprint: "J4K9-72QF",
      display_name: "Jordan P.",
      handle: "jordanp",
      invite_id: "inv_free_code",
      invite_source: "Free Space join code",
      join_method: "code",
      join_request_id: "jr_free_jordan",
      prior_ban_in_space: false,
      profile_age_label: "Profile created 18 days ago",
      profile_id: "pro_jordan",
      space_id: "spc_free_field_ops",
      verification_state: "device_seen"
    })
  ],
  spc_plus_training: [
    joinRequest({
      application_answer: "I joined the workshop waitlist and was invited by the organiser.",
      device_fingerprint: "M8Q2-11LK",
      display_name: "Avery S.",
      handle: "averys",
      invite_id: "inv_plus_code",
      invite_source: "Workshop code",
      join_method: "code",
      join_request_id: "jr_plus_avery",
      prior_ban_in_space: false,
      profile_age_label: "Profile created 7 days ago",
      profile_id: "pro_avery",
      space_id: "spc_plus_training",
      verification_state: "not_checked"
    })
  ],
  spc_paid_market_ops: [
    joinRequest({
      application_answer: "I am part of the weekend vendor crew and need guest updates.",
      device_fingerprint: "R2VA-90MC",
      display_name: "Morgan R.",
      handle: "morganr",
      invite_id: "inv_paid_qr",
      invite_source: "Market QR poster",
      join_method: "qr",
      join_request_id: "jr_paid_morgan",
      prior_ban_in_space: true,
      profile_age_label: "Profile created 2 months ago",
      profile_id: "pro_morgan",
      space_id: "spc_paid_market_ops",
      verification_state: "verified_by_admin"
    })
  ]
};

export const MOCK_REPORTS: readonly MockReport[] = [
  {
    report_id: "rep_paid_1",
    space_id: "spc_paid_market_ops",
    room_id: "room_paid_general",
    reporter_member_id: "mem_paid_1",
    reported_member_id: "mem_paid_2",
    category: "harassment_abuse",
    severity: "normal",
    status: "open",
    submitted_at: "2026-06-15T12:30:00Z",
    resolved_at: null,
    ai_moderation_result: "Placeholder only: likely policy concern, needs human review.",
    submitted_plaintext: "Selected reported text excerpt for moderator review.",
    review_note: "Reported content is visible here only because a member explicitly submitted it for review."
  },
  {
    report_id: "rep_free_1",
    space_id: "spc_free_field_ops",
    room_id: "room_free_general",
    reporter_member_id: "mem_free_admin",
    reported_member_id: null,
    category: "spam_scam",
    severity: "normal",
    status: "reviewing",
    submitted_at: "2026-06-14T10:15:00Z",
    resolved_at: null,
    ai_moderation_result: "Placeholder only: possible spam pattern, not an automatic decision.",
    submitted_plaintext: "Short submitted excerpt attached to a report.",
    review_note: "Ordinary retained messages remain outside admin mock tables."
  }
];

export const MOCK_PAID_SPACE_SLOTS: readonly PaidSpaceSlot[] = [
  {
    paid_slot_id: "slot_mock_1",
    owner_profile_id: "pro_mock_owner",
    subscription_id: "sub_spaces_3_mock",
    slot_index: 1,
    assigned_space_id: "spc_paid_market_ops",
    active: true,
    status: "assigned",
    current_period_ends_at: "2026-07-16T00:00:00Z"
  },
  {
    paid_slot_id: "slot_mock_2",
    owner_profile_id: "pro_mock_owner",
    subscription_id: "sub_spaces_3_mock",
    slot_index: 2,
    assigned_space_id: null,
    active: true,
    status: "unassigned",
    current_period_ends_at: "2026-07-16T00:00:00Z"
  },
  {
    paid_slot_id: "slot_mock_3",
    owner_profile_id: "pro_mock_owner",
    subscription_id: "sub_spaces_3_mock",
    slot_index: 3,
    assigned_space_id: null,
    active: true,
    status: "unassigned",
    current_period_ends_at: "2026-07-16T00:00:00Z"
  }
];

export const FREE_LIMIT_MODE_EXAMPLE: FreeLimitModeExample = {
  space_id: "spc_free_field_ops",
  space_name: "Field Operations Team",
  started_at: "2026-06-12T09:00:00Z",
  grace_days_total: 14,
  grace_days_remaining: 10,
  reason: "This Space is over the Free member limit during the grace period."
};

export const MOCK_ADMIN_DATA: MockAdminData = {
  profile,
  spaces: MOCK_SPACES,
  roomsBySpaceId: MOCK_ROOMS_BY_SPACE_ID,
  membersBySpaceId: MOCK_MEMBERS_BY_SPACE_ID,
  invitesBySpaceId: MOCK_INVITES_BY_SPACE_ID,
  joinRequestsBySpaceId: MOCK_JOIN_REQUESTS_BY_SPACE_ID,
  reports: MOCK_REPORTS,
  paidSpaceSlots: MOCK_PAID_SPACE_SLOTS,
  freeLimitModeExample: FREE_LIMIT_MODE_EXAMPLE
};

export const ADMIN_DASHBOARD_METRICS: readonly AdminMetric[] = [
  {
    title: "Spaces you manage",
    value: String(MOCK_SPACES.length),
    note: "Mock count for owned or admin-managed Spaces."
  },
  {
    title: "Pending join requests",
    value: String(flattenRecord(MOCK_JOIN_REQUESTS_BY_SPACE_ID).filter((request) => request.status === "pending").length),
    note: "Requests waiting for owner or admin review."
  },
  {
    title: "Open reports",
    value: String(MOCK_REPORTS.filter((report) => report.status === "open" || report.status === "reviewing").length),
    note: "Report queue mock data. Reported content may be reviewed only through explicit report workflows."
  },
  {
    title: "Relay status",
    value: "Healthy",
    note: "Mock operational state for encrypted Relay availability."
  },
  {
    title: "Billing alerts",
    value: "0",
    note: "Future alerts for payment, renewal, and entitlement issues."
  }
];

export const MANAGED_SPACES_ROWS: readonly AdminTableRow[] = MOCK_SPACES.map((space) => ({
  label: space.name,
  detail: `${space.plan_label} - ${memberCount(space.space_id)} of ${space.member_cap} members - ${roomCount(space.space_id)} Rooms - ${space.retention_days}-day retention`,
  status: space.free_limit_mode ? "Free Limit Mode" : space.paid_slot_id ? "Slot assigned" : "Active"
}));

export const JOIN_REQUEST_ROWS: readonly AdminTableRow[] = flattenRecord(MOCK_JOIN_REQUESTS_BY_SPACE_ID).map((request) => ({
  label: request.profile_display_name ?? request.profile_handle ?? request.profile_id,
  detail: `Requested access to ${spaceName(request.space_id)}`,
  status: request.status === "pending" ? "Pending" : request.status
}));

export const PAID_SLOT_USAGE = {
  assigned: MOCK_PAID_SPACE_SLOTS.filter((slot) => slot.assigned_space_id !== null && slot.active).length,
  total: MOCK_PAID_SPACE_SLOTS.filter((slot) => slot.active).length,
  label: "Spaces 3",
  note: "Assign paid slots to Spaces you own. Paid Spaces do not count against personal Space creation limits."
} as const;

export const SPACE_DASHBOARD_ROWS: readonly SpaceDashboardRow[] = MOCK_SPACES.map((space) => ({
  spaceId: space.space_id,
  name: space.name,
  planState: space.plan_label,
  memberCount: memberCount(space.space_id),
  memberCap: space.member_cap,
  roomCount: roomCount(space.space_id),
  roomCap: space.room_cap,
  retentionLabel: retentionLabel(space),
  role: roleForProfile(space.space_id, profile.profile_id),
  relayStatus: formatRelayStatus(space.relay_status),
  freeLimitMode: space.free_limit_mode
}));

export function getMockSpaceAdminDetail(spaceId: string): SpaceAdminDetail | null {
  const space = MOCK_SPACES.find((candidate) => candidate.space_id === spaceId);

  if (!space) {
    return null;
  }

  const rooms = MOCK_ROOMS_BY_SPACE_ID[space.space_id] ?? [];
  const members = MOCK_MEMBERS_BY_SPACE_ID[space.space_id] ?? [];
  const invites = MOCK_INVITES_BY_SPACE_ID[space.space_id] ?? [];
  const joinRequests = MOCK_JOIN_REQUESTS_BY_SPACE_ID[space.space_id] ?? [];
  const reports = MOCK_REPORTS.filter((report) => report.space_id === space.space_id);
  const paidSlot = MOCK_PAID_SPACE_SLOTS.find((slot) => slot.assigned_space_id === space.space_id) ?? null;

  return {
    space,
    rooms,
    members,
    invites,
    joinRequests,
    reports,
    paidSlot,
    currentUserRole: roleForProfile(space.space_id, profile.profile_id),
    memberCount: members.length,
    roomCount: rooms.length,
    pendingJoinRequestCount: joinRequests.filter((request) => request.status === "pending").length,
    openReportCount: reports.filter((report) => report.status === "open" || report.status === "reviewing").length,
    retentionLabel: retentionLabel(space),
    relayStatusLabel: formatRelayStatus(space.relay_status)
  };
}

function room(
  room_id: string,
  space_id: string,
  name: string,
  description: string,
  key_epoch: number,
  role_restricted = false,
  allowed_roles: readonly SpaceRole[] = [],
  frozen = false,
  notice: string | null = null
): MockRoom {
  return {
    room_id,
    space_id,
    name,
    description,
    role_restricted,
    allowed_roles,
    required_role: null,
    key_epoch,
    created_at: "2026-05-01T08:00:00Z",
    updated_at: NOW,
    room_type: role_restricted ? "role_based" : "standard",
    frozen,
    notice
  };
}

function member(
  member_id: string,
  space_id: string,
  profile_id: string,
  role: SpaceRole,
  display_name: string,
  handle: string,
  device_fingerprint: string,
  guest_expires_at: string | null = null
): MockMember {
  return {
    member_id,
    space_id,
    profile_id,
    role,
    display_name,
    handle,
    joined_at: "2026-05-02T08:00:00Z",
    created_at: "2026-05-02T08:00:00Z",
    updated_at: NOW,
    removed_at: null,
    status: "active",
    guest_expires_at,
    device_fingerprint
  };
}

function invite(
  invite_id: string,
  space_id: string,
  invite_type: Invite["invite_type"],
  token_hint: string,
  use_count: number,
  label: string,
  raw_token_once: string | null,
  guest_pass_expires_at: string | null = null
): MockInvite {
  return {
    invite_id,
    space_id,
    invite_type,
    token_hint,
    use_count,
    expires_at: "2026-06-23T09:00:00Z",
    created_at: "2026-06-16T09:00:00Z",
    revoked_at: null,
    label,
    raw_token_once,
    guest_pass_expires_at
  };
}

function joinRequest(input: {
  readonly application_answer: string;
  readonly device_fingerprint: string;
  readonly display_name: string;
  readonly handle: string;
  readonly invite_id: string;
  readonly invite_source: string;
  readonly join_method: MockJoinRequest["join_method"];
  readonly join_request_id: string;
  readonly prior_ban_in_space: boolean;
  readonly profile_age_label: string;
  readonly profile_id: string;
  readonly space_id: string;
  readonly verification_state: MockJoinRequest["verification_state"];
}): MockJoinRequest {
  return {
    join_request_id: input.join_request_id,
    space_id: input.space_id,
    profile_id: input.profile_id,
    profile_display_name: input.display_name,
    profile_handle: input.handle,
    invite_id: input.invite_id,
    status: "pending",
    request_note: input.application_answer,
    requested_at: "2026-06-16T08:30:00Z",
    expires_at: "2026-06-23T08:30:00Z",
    resolved_at: null,
    join_method: input.join_method,
    invite_source: input.invite_source,
    application_answer: input.application_answer,
    verification_state: input.verification_state,
    device_fingerprint: input.device_fingerprint,
    profile_age_label: input.profile_age_label,
    prior_ban_in_space: input.prior_ban_in_space
  };
}

function flattenRecord<T>(record: Readonly<Record<string, readonly T[]>>): readonly T[] {
  return Object.values(record).flat();
}

function memberCount(spaceId: string): number {
  return MOCK_MEMBERS_BY_SPACE_ID[spaceId]?.length ?? 0;
}

function roomCount(spaceId: string): number {
  return MOCK_ROOMS_BY_SPACE_ID[spaceId]?.length ?? 0;
}

function spaceName(spaceId: string): string {
  return MOCK_SPACES.find((space) => space.space_id === spaceId)?.name ?? spaceId;
}

function retentionLabel(space: MockSpace): string {
  return space.retention_options
    ? `${space.retention_days} days (${space.retention_options.join("/")})`
    : `${space.retention_days} days`;
}

function roleForProfile(spaceId: string, profileId: string): string {
  const role = MOCK_MEMBERS_BY_SPACE_ID[spaceId]
    ?.find((memberRecord) => memberRecord.profile_id === profileId)
    ?.role;

  return role ? capitalize(role) : "member";
}

function formatRelayStatus(status: MockRelayStatus): string {
  switch (status) {
    case "priority":
      return "Priority Relay";
    case "healthy":
      return "Healthy";
    case "degraded":
      return "Degraded";
    case "standard":
      return "Standard Relay";
  }
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
